import { useEffect } from 'react';

/**
 * Observes the DOM for the appearance of a result message container
 * rendered by the Viamobi component and scrolls it into view.
 *
 * Uses a MutationObserver to detect when an element matching the given
 * CSS selector is added to the DOM, then calls scrollIntoView on it.
 * Gracefully degrades to a no-op if MutationObserver is not supported.
 *
 * @param {string} selector - CSS selector for the result container
 * @param {ScrollIntoViewOptions} [options={ behavior: 'smooth', block: 'center' }] - scrollIntoView options
 */
export function useAutoScrollToResult(
  selector,
  options = { behavior: 'smooth', block: 'center' }
) {
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    if (!selector) {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) {
            continue;
          }

          const element = /** @type {Element} */ (node);

          // Check if the added node itself matches
          if (element.matches(selector)) {
            element.scrollIntoView(options);
            return;
          }

          // Check if any descendant of the added node matches
          const descendant = element.querySelector(selector);
          if (descendant) {
            descendant.scrollIntoView(options);
            return;
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [selector, options]);
}
